import React, { FC, useRef, useEffect } from "react";
import {
  Container,
  Title,
  QuestionCard,
  QuestionTitle,
  Answer,
  CtaWrapper,
  Subtitle,
  StyledContainer,
} from "./styles";
import ButtonLink from "../shared/ButtonLink";
import { CardType } from "../CookItAtHome/Card";
import { isMobile } from "react-device-detect";

export type InfiniteHorizontalCardsProps = {
  slug?: string;
  title?: string;
  subtitle?: string;
  cards?: {
    title: string;
    description: string;
    link?: string;
  }[];
  button?: { label: string; link: string };
  backgroundColor?: string;
  color?: string;
  cardColor?: string;
  isPublished?: boolean;
};

const infiniteHorizontalContent = {
  isPublished: true,
  backgroundColor: '#FDF2D6',
  color: '#033305',
  cardColor: '#FDC04E',
  title: 'Debunking Common Misconceptions',
  subtitle:
    `There's a lot of misinformation out there, so we're setting the record straight.`,
  cards: [
    {
      title:
        'Is plant-based meat ultra processed and unhealthy?',
      description:
        `Not all processed foods are created equal. WHO-backed study links processed meats to disease risk—but not plant-based alternatives. Read more <a href="/whats-new/debunking-common-misconceptions" aria-label="navigate to blog about debunking common misconceptions">here</a>.`,
    },
    {
      title:
        'Is plant-based meat full of chemicals?',
      description:
        `Beyond products are made from clean & simple plant-based ingredients. Check out Beyond Beef's new ingredient list, now with avocado oil. Read more <a href="/whats-new/debunking-common-misconceptions" aria-label="navigate to blog about debunking common misconceptions">here</a>.`,
    },
    {
      title: 'Is Beyond Meat high in sodium?',
      description:
        `Comparing our products to raw, unseasoned beef misses the point—most people season their meat with salt or spices. Read more <a href="/whats-new/debunking-common-misconceptions" aria-label="navigate to blog about debunking common misconceptions">here</a>.`,
    },
    {
      title: 'Is animal meat “pure and nutrient dense”?',
      description:
        `Animal meat is linked to high cholesterol and an increased risk of heart disease. Processed meat is classified as a carcinogen by the WHO. Read more <a href="/whats-new/debunking-common-misconceptions" aria-label="navigate to blog about debunking common misconceptions">here</a>.`,
    },
    {
      title: 'Are seed oils unhealthy?',
      description:
        `There's no solid scientific evidence proving they're harmful. Most criticism comes from misleading social media hype, not credible research. Read more <a href="/whats-new/debunking-common-misconceptions" aria-label="navigate to blog about debunking common misconceptions">here</a>.`,
    },
    {
      title: 'Do only vegans and vegetarians eat Beyond Meat?',
      description:
        `Over 80% of Beyond Meat consumers eat both plant-based meat and animal meat (source: Primary Quantitative Research US August 2024, n=4,000+ US respondents).`,
    },
  ],
};

const InfiniteHorizontalCards: FC<InfiniteHorizontalCardsProps> =
  ({
    title = infiniteHorizontalContent.title,
    cards = infiniteHorizontalContent.cards,
    subtitle = infiniteHorizontalContent.subtitle,
    button,
    slug,
    backgroundColor = infiniteHorizontalContent.backgroundColor,
    color = infiniteHorizontalContent.color,
    cardColor = infiniteHorizontalContent.cardColor,
    isPublished = infiniteHorizontalContent.isPublished,
  }: InfiniteHorizontalCardsProps) => {
    if (cards?.length === 0 || !isPublished) {
      return null;
    }

    const scrollRef = useRef<HTMLElement | null>();
    const pauseScroll = useRef(false);
    const shouldAddOffset = (index: number): boolean => {
      return index % 2 === 0;
    };

    useEffect(() => {
      const iId = setInterval(
        () => {
          if (!scrollRef.current) return;
          try {
            if (
              scrollRef?.current?.scrollLeft >=
                (scrollRef?.current?.scrollWidth / 3) * 2 ||
              scrollRef?.current?.scrollLeft === 0
            ) {
              scrollRef.current.scrollLeft =
                scrollRef?.current?.scrollWidth / 3;
              return;
            }
            if (!pauseScroll.current)
              scrollRef.current.scrollLeft =
                scrollRef.current.scrollLeft + 1;
          } catch (e) {
            console.log(e);
          }
        },
        isMobile ? 30 : 10,
      );
      return () => {
        clearInterval(iId);
      };
    }, []);

    return (
      <Container
        id={slug}
        backgroundColor={backgroundColor}
        color={color}
        cardColor={cardColor}
      >
        {title && <Title>{title}</Title>}
        {subtitle && <Subtitle>{subtitle}</Subtitle>}

        <StyledContainer
          innerRef={(r) => (scrollRef.current = r)}
          activationDistance={0}
        >
          {[
            ...cards,
            ...cards,
            ...cards,
            ...cards,
            ...cards,
          ]?.map((card: Partial<CardType>, i: number) => (
            <QuestionCard
              as={card?.link ? "a" : "div"}
              href={card?.link}
              key={i}
              className={`${
                shouldAddOffset(i) ? "with-card-offset" : ""
              }`}
              onMouseEnter={() =>
                (pauseScroll.current = true)
              }
              onMouseLeave={() =>
                (pauseScroll.current = false)
              }
            >
              {card?.title && (
                <QuestionTitle>{card?.title}</QuestionTitle>
              )}
              <Answer
                dangerouslySetInnerHTML={{
                  __html: card?.description ?? "",
                }}
              />
            </QuestionCard>
          ))}
        </StyledContainer>
        {button?.label && (
          <CtaWrapper>
            <ButtonLink
              label={button?.label}
              link={button?.link}
            />
          </CtaWrapper>
        )}
      </Container>
    );
  };

export default InfiniteHorizontalCards;
